import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { ROUTES } from '@/constants/routes';
import { useFeatureFlags } from '@/contexts';

import {
  CycleFieldsIcon,
  SeasonsManagementIcon,
  DashboardIcon,
  FieldAllocationIcon,
  ParentSeedsAssignmentIcon,
  ProductionOrderIcon,
  SettingsIcon
} from '../Icons';

import { SidebarMenu } from './components';
import type { SidebarMenuItemModel } from './types';

const SideMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isEnabled } = useFeatureFlags();

  const items: SidebarMenuItemModel[] = useMemo<SidebarMenuItemModel[]>(
    () =>
      [
        {
          key: ROUTES.FIELD_ALLOCATION,
          label: 'Field Allocations',
          icon: <FieldAllocationIcon />,
          onClick: () => {
            void navigate(ROUTES.FIELD_ALLOCATION);
          }
        },
        {
          key: ROUTES.PRODUCTION_ORDER_LIST,
          label: 'Production Order Management',
          icon: <ProductionOrderIcon />,
          onClick: () => {
            void navigate(ROUTES.PRODUCTION_ORDER_LIST);
          }
        },
        {
          key: 'parent-seeds',
          label: 'Input Batches',
          icon: <ParentSeedsAssignmentIcon />,
          children: [
            {
              key: ROUTES.AGRONOMIC_DEMAND,
              label: 'Agronomic Demand',
              icon: <ParentSeedsAssignmentIcon />,
              onClick: () => {
                void navigate(ROUTES.AGRONOMIC_DEMAND);
              }
            },
            {
              key: ROUTES.PARENT_SEEDS_ASSIGNMENT,
              label: 'Assignment',
              icon: <ParentSeedsAssignmentIcon />,
              onClick: () => {
                void navigate(ROUTES.PARENT_SEEDS_ASSIGNMENT);
              }
            },
            {
              key: ROUTES.EXPORT_BATCHES,
              label: 'Export Batches',
              icon: <ParentSeedsAssignmentIcon />,
              onClick: () => {
                void navigate(ROUTES.EXPORT_BATCHES);
              }
            }
          ]
        },
        {
          key: 'other-options',
          label: 'Other Options',
          icon: <SettingsIcon />,
          children: [
            {
              key: ROUTES.SEASONS_MANAGEMENT,
              label: 'Seasons Management',
              icon: <SeasonsManagementIcon />,
              onClick: () => {
                void navigate(ROUTES.SEASONS_MANAGEMENT);
              }
            },
            {
              key: ROUTES.CYCLE_FIELDS,
              label: 'Season Fields',
              icon: <CycleFieldsIcon />,
              onClick: () => {
                void navigate(ROUTES.CYCLE_FIELDS);
              }
            },
            {
              key: ROUTES.GROWER_LIST,
              label: 'Grower Management',
              icon: <CycleFieldsIcon />,
              onClick: () => {
                void navigate(ROUTES.GROWER_LIST);
              }
            },
            {
              key: ROUTES.GROWING_SUBGROWING_AREAS,
              label: 'Growing Area Management',
              icon: <CycleFieldsIcon />,
              onClick: () => {
                void navigate(ROUTES.GROWING_SUBGROWING_AREAS);
              }
            },
            {
              key: ROUTES.VARIETY_DEVIATION,
              label: 'Variety Deviation',
              icon: <DashboardIcon />,
              onClick: () => {
                void navigate(ROUTES.VARIETY_DEVIATION);
              }
            },
            {
              key: ROUTES.WORKER_LIST,
              label: 'Workers Management',
              icon: <DashboardIcon />,
              onClick: () => {
                void navigate(ROUTES.WORKER_LIST);
              }
            },
            {
              key: ROUTES.PARENT_SEED_ATTRIBUTE_LIST,
              label: 'Parent Seeds Attributes',
              icon: <DashboardIcon />,
              onClick: () => {
                void navigate(ROUTES.PARENT_SEED_ATTRIBUTE_LIST);
              }
            },
            {
              key: ROUTES.PRODUCTION_INSTRUCTIONS,
              label: 'Production Instructions',
              icon: <DashboardIcon />,
              onClick: () => {
                void navigate(ROUTES.PRODUCTION_INSTRUCTIONS);
              }
            },
            {
              key: ROUTES.PRODUCTION_INSTRUCTIONS_TEMPLATE,
              label: 'Production Instructions Template',
              icon: <DashboardIcon />,
              onClick: () => {
                void navigate(ROUTES.PRODUCTION_INSTRUCTIONS_TEMPLATE);
              },
              hidden: !isEnabled('configureWebProductionInstructionsTemplate')
            },
            {
              key: ROUTES.REVIEW_SPP_DATA,
              label: 'Review SPP Data',
              icon: <DashboardIcon />,
              onClick: () => {
                void navigate(ROUTES.REVIEW_SPP_DATA);
              },
              hidden: !isEnabled('releaseWebReviewSppData')
            },
            {
              key: ROUTES.DATA_IMPORT,
              label: 'Data Import',
              icon: <DashboardIcon />,
              onClick: () => {
                void navigate(ROUTES.DATA_IMPORT);
              },
              hidden: !isEnabled('releaseWebDataImport')
            },
            {
              key: ROUTES.CROPWISE_INTEGRATIONS,
              label: 'Cropwise Integrations',
              icon: <DashboardIcon />,
              onClick: () => {
                void navigate(ROUTES.CROPWISE_INTEGRATIONS);
              },
              hidden: !isEnabled('configureWebCropwiseIntegrationsScreen')
            }
          ]
        }
      ].reduce((itemsList, item: SidebarMenuItemModel) => {
        if (item.hidden) {
          return itemsList;
        }

        if (item.children) {
          item.children = item.children.filter(child => !child.hidden);
        }

        return [...itemsList, item];
      }, [] as SidebarMenuItemModel[]),
    [navigate, isEnabled]
  );

  return <SidebarMenu list={items} activeKey={location.pathname} />;
};

export default SideMenu;
